/**
 * Builds a context ID by combining a prefix with one or more string parts.
 * This is the standard format for all analytics `context_id` values in PMS.
 *
 * Use underscores (`_`) to separate high-level scopes (e.g. `scheduled_consult_tab`)
 * Use hyphens (`-`) to separate nested parts like sections or components
 *    (e.g. `scheduled_consult_tab-prescriptions-mock_section`)
 *
 * This format ensures consistent, structured tracking across tabs, sections, and components.
 *
 * @param prefix - The top-level context (e.g. "scheduled_consult_tab")
 * @param parts - Additional parts to append (e.g. "prescriptions", "mock_section")
 *
 * @returns A hyphenated context ID string following PMS analytics standards.
 *
 * @example
 * prefixContextIdChain("scheduled_consult_tab")
 * // → "scheduled_consult_tab"
 *
 * prefixContextIdChain("scheduled_consult_tab", "prescriptions", "mock_section")
 * // → "scheduled_consult_tab-prescriptions-mock_section"
 *
 * prefixContextIdChain("scheduled_consult_tab", "scheduled_consult_tab-prescriptions")
 * // → "scheduled_consult_tab-prescriptions"
 */

export const buildContextId = (prefix: string, ...parts: string[]): string => {
  const filteredParts = parts.filter((part) => part.trim() !== '');
  const suffix = filteredParts.join('-');

  if (suffix.startsWith(prefix)) {
    return suffix;
  }

  return suffix ? `${prefix}-${suffix}` : prefix;
};
